import { useCallback } from 'react';
import { useAnalytics } from '@context/analytics/analytics.context';
import {
  aiListingAddPhotosEventPayload,
  aiListingBannedBrandEventPayload,
  aiListingDeletePhotoEventPayload,
  aiListingDescriptionEventPayload,
  aiListingErrorEventPayload,
  aiListingFieldEditEventPayload,
  aiListingFieldSaveEventPayload,
  aiListingFormSubmitEventPayload,
  aiListingLeaveConfirmationEventPayload,
  aiListingMeasurementGuideEventPayload,
  aiListingPhotoReorderEventPayload,
  aiListingScanEventPayload,
  aiListingModelEventPayload,
  aiListingPurchaseInformationEventPayload,
  aiListingSellButtonEventPayload,
  aiListingSizeEventPayload,
  aiListingStartEventPayload,
  aiListingTypeaheadSelectEventPayload,
  aiListingViewDraftsEventPayload,
  aiListingViewGuidelinesEventPayload,
} from '@tracking/ai-listing/ai-listing-tracking';
import {
  AiListingDeletePhotoEvent,
  AiListingDescriptionEvent,
  AiListingBannedBrandEvent,
  AiListingErrorEvent,
  AiListingFieldEditEvent,
  AiListingFieldSaveEvent,
  AiListingTypeaheadSelectEvent,
  AiListingFormSubmitEvent,
  AiListingLeaveConfirmationEvent,
  AiListingModelEvent,
  AiListingPurchaseInformationEvent,
  AiListingSizeEvent,
  AiListingStartEvent,
} from '@interfaces/models/internal/analytics-event/ai-listing-event';

/**
 * Tracking hook for the AI listing flow.
 *
 * Depends only on `useAnalytics()` (never `useAiListing()`), so it can be used
 * from components rendered outside the `AiListingProvider` — e.g. the
 * `ListingOptionsButton` in the header / sell page. Per-call dynamic values are
 * passed as arguments.
 */
export const useAiListingTracking = () => {
  const { sendEvent } = useAnalytics();

  const trackListingStart = useCallback(
    (action: AiListingStartEvent['action']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingStartEventPayload, action },
      });
    },
    [sendEvent],
  );

  const trackPhotoScan = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingScanEventPayload });
  }, [sendEvent]);

  const trackAddPhotos = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingAddPhotosEventPayload });
  }, [sendEvent]);

  const trackPhotoReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingPhotoReorderEventPayload, label: fromIndex, value: toIndex },
      });
    },
    [sendEvent],
  );

  const trackLeaveConfirmation = useCallback(
    (action: AiListingLeaveConfirmationEvent['action'], label: AiListingLeaveConfirmationEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingLeaveConfirmationEventPayload, action, label },
      });
    },
    [sendEvent],
  );

  const trackFieldEdit = useCallback(
    (action: AiListingFieldEditEvent['action'], label: AiListingFieldEditEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingFieldEditEventPayload, action, label },
      });
    },
    [sendEvent],
  );

  const trackDescription = useCallback(
    (label: AiListingDescriptionEvent['label']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingDescriptionEventPayload, label },
      });
    },
    [sendEvent],
  );

  const trackFormSubmit = useCallback(
    (action: AiListingFormSubmitEvent['action']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingFormSubmitEventPayload, action },
      });
    },
    [sendEvent],
  );

  const trackSellAnItem = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingSellButtonEventPayload });
  }, [sendEvent]);

  const trackViewDrafts = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingViewDraftsEventPayload });
  }, [sendEvent]);

  const trackViewGuidelines = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingViewGuidelinesEventPayload });
  }, [sendEvent]);

  const trackDeletePhoto = useCallback(
    (label: AiListingDeletePhotoEvent['label']) => {
      sendEvent({ type: 'event', payload: { ...aiListingDeletePhotoEventPayload, label } });
    },
    [sendEvent],
  );

  const trackError = useCallback(
    (label: AiListingErrorEvent['label'], property?: AiListingErrorEvent['property']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingErrorEventPayload, label, ...(property ? { property } : {}) },
      });
    },
    [sendEvent],
  );

  const trackTypeaheadSelect = useCallback(
    (
      action: AiListingTypeaheadSelectEvent['action'],
      label: AiListingTypeaheadSelectEvent['label'],
      property: AiListingTypeaheadSelectEvent['property'],
    ) => {
      sendEvent({ type: 'event', payload: { ...aiListingTypeaheadSelectEventPayload, action, label, property } });
    },
    [sendEvent],
  );

  const trackFieldSave = useCallback(
    (
      action: AiListingFieldSaveEvent['action'],
      label: AiListingFieldSaveEvent['label'],
      property: AiListingFieldSaveEvent['property'],
    ) => {
      sendEvent({ type: 'event', payload: { ...aiListingFieldSaveEventPayload, action, label, property } });
    },
    [sendEvent],
  );

  const trackSize = useCallback(
    (label: AiListingSizeEvent['label']) => {
      sendEvent({ type: 'event', payload: { ...aiListingSizeEventPayload, label } });
    },
    [sendEvent],
  );

  const trackPurchaseInformation = useCallback(
    (label: AiListingPurchaseInformationEvent['label'], property?: AiListingPurchaseInformationEvent['property']) => {
      sendEvent({
        type: 'event',
        payload: { ...aiListingPurchaseInformationEventPayload, label, ...(property ? { property } : {}) },
      });
    },
    [sendEvent],
  );

  const trackModel = useCallback(
    (label: AiListingModelEvent['label']) => {
      sendEvent({ type: 'event', payload: { ...aiListingModelEventPayload, label } });
    },
    [sendEvent],
  );

  const trackMeasurementGuide = useCallback(() => {
    sendEvent({ type: 'event', payload: aiListingMeasurementGuideEventPayload });
  }, [sendEvent]);

  const trackBannedBrand = useCallback(
    (property: AiListingBannedBrandEvent['property']) => {
      sendEvent({ type: 'event', payload: { ...aiListingBannedBrandEventPayload, property } });
    },
    [sendEvent],
  );

  return {
    trackListingStart,
    trackPhotoScan,
    trackAddPhotos,
    trackPhotoReorder,
    trackLeaveConfirmation,
    trackFieldEdit,
    trackDescription,
    trackFormSubmit,
    trackSellAnItem,
    trackViewDrafts,
    trackViewGuidelines,
    trackDeletePhoto,
    trackError,
    trackTypeaheadSelect,
    trackFieldSave,
    trackBannedBrand,
    trackMeasurementGuide,
    trackSize,
    trackModel,
    trackPurchaseInformation,
  };
};
